import { useCallbackRef } from "../lifecycle";
import { updateCheckoutState } from "src/utils/redux/slices/checkout";
import {
  createOrderRequest,
  getOrderRequest,
  executeOrderRequest,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { useAppDispatch } from "../redux";
import {
  setCheckoutOrders,
  setHasInitializationFailed,
  setCheckoutCustomerDetails,
  setCheckoutOperationPromoCode,
  setPurchaseOperation,
  setSwitchOperation,
  SaleItemInfo,
} from "src/utils/redux/slices/checkoutOrders";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { convertCyclesToPlan } from "src/utils/orderConversions";
import {
  legacyPurchaseRoot,
  setRecommendedPlan,
} from "src/utils/redux/slices/recommendedPlan";
import { captureException } from "src/utils/error";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { convertMoneyProtoToNumber } from "src/utils/noomContracts/moneyProto";
import {
  setPromoCode,
  updatePromoDiscountAmount,
  setPromoError,
  clearPromoCode,
} from "src/utils/redux/slices/promoCode";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import getStore from "src/utils/redux/store";
import {
  isOrdersPreviewPurchaseCheckout,
  isOrdersCheckout,
} from "src/utils/userSegment/features";
import { prefetchEltvIfEligible } from "src/hooks/checkout/eltvPrefetch";
import { getActiveUser } from "@noom/hive-auth";
import {
  extractBillingCycles,
  getPromoCodeApplicationResult,
  getRefundAmount,
} from "src/utils/ordersUtils";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import {
  getPromoCodeApplicationError,
  PromoCodeFailureReason,
} from "src/utils/promo";
import { SwitchOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";

function remapURLNoomPlanId(noomPlanId: string) {
  if (noomPlanId && /\/purchase(-hm)?\//.test(window.location.pathname)) {
    const { history } = window;
    history.replaceState(
      history.state,
      "",
      legacyPurchaseRoot() + window.location.hash
    );
  }
}

export function useOrdersCheckoutMethods() {
  const dispatch = useAppDispatch();
  const accessCode = getActiveUser()?.accessCode;

  function setPromoCodeStateFromOrder(
    order: OrderProto,
    recurringBillingCycle?: BillingCyclePreviewProto
  ) {
    const result = getPromoCodeApplicationResult(order);

    if (!result) {
      dispatch(clearPromoCode());
      return;
    }

    const recurring = recurringBillingCycle?.invoice?.lineItems?.[0];
    const discountAmount = convertMoneyProtoToNumber(
      recurring?.discountInfo?.promoAmount
    );
    dispatch(updatePromoDiscountAmount(discountAmount));

    if (result.isSuccessful) {
      dispatch(
        setPromoCode({
          promoCodeError: "",
          promoCodeErrorType: null,
          promoCodeApplied: result.promoCode,
        })
      );
      return;
    }

    dispatch(setCheckoutOperationPromoCode(undefined));
    dispatch(
      setPromoError(
        result.errorState ??
          getPromoCodeApplicationError(PromoCodeFailureReason.Unknown)
      )
    );
  }

  async function setOrder(order: OrderProto) {
    const state = getStore().getState();

    const { saleItem } = state.checkoutOrders;
    const { trialSaleItemId, pspPlanId, legacyNoomPlanId } = saleItem;
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(order);
    const refundAmount = getRefundAmount(order);
    dispatch(
      setCheckoutOrders({
        order,
        initialBillingCycle,
        recurringBillingCycle,
        refundAmount,
      })
    );
    // All the med checkout UI was migrated to use the new checkout orders slice
    // but we still need to convert the plan to the recommended plan for the pixel tracking,
    // purchase logic and purchase tracking that have not been migrated yet.
    // TODO: MGEXP-3025
    const recommendedPlan = convertCyclesToPlan({
      initialCycle: initialBillingCycle,
      recurringCycle: recurringBillingCycle,
      pspPlanId,
      legacyNoomPlanId,
      trialSaleItemId,
    });
    dispatch(setRecommendedPlan(recommendedPlan));
    remapURLNoomPlanId(legacyNoomPlanId);
    setPromoCodeStateFromOrder(order, recurringBillingCycle);

    prefetchEltvIfEligible();

    return { order, initialBillingCycle, recurringBillingCycle };
  }

  async function createOrder(): Promise<{
    order: OrderProto;
    initialBillingCycle?: BillingCyclePreviewProto;
    recurringBillingCycle?: BillingCyclePreviewProto;
  }> {
    const usePreviewMode =
      isOrdersPreviewPurchaseCheckout() && !isOrdersCheckout();
    const state = getStore().getState();
    // Get the sale item directly from state to avoid using stale state from the hook
    const { customerDetails, operation } = state.checkoutOrders;

    const order = await createOrderRequest(
      operation,
      usePreviewMode ? undefined : accessCode,
      usePreviewMode,
      customerDetails
    );
    return setOrder(order);
  }

  async function createPurchaseOrder(payload: {
    saleItem: SaleItemInfo;
    promoCode?: string;
  }) {
    dispatch(setPurchaseOperation(payload));
    return createOrder();
  }

  async function createSwitchOrder(payload: {
    saleItem: SaleItemInfo;
    switchOperation: SwitchOperationProto;
  }) {
    dispatch(setSwitchOperation(payload));
    return createOrder();
  }

  async function getOrder(orderUuid: string) {
    const newOrder = await getOrderRequest({ orderUuid });

    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(newOrder);

    const trialSaleItemId =
      initialBillingCycle?.invoice?.lineItems?.[0]?.saleItemId;

    const newRecommendedPlan = convertCyclesToPlan({
      initialCycle: initialBillingCycle,
      recurringCycle: recurringBillingCycle,
      pspPlanId: null,
      legacyNoomPlanId: null,
      trialSaleItemId,
    });

    dispatch(setRecommendedPlan(newRecommendedPlan));

    dispatch(
      setCheckoutOrders({
        operation: newOrder.operations?.[0],
        saleItem: {
          saleItemId:
            recurringBillingCycle?.invoice?.lineItems?.[0]?.saleItemId,
          trialSaleItemId,
        },
        order: newOrder,
        initialBillingCycle,
        recurringBillingCycle,
      })
    );
  }

  async function initializeCheckout() {
    const { saleItem } = getStore().getState().checkoutOrders;
    const task = trackTask("InitializeOrdersCheckout", {
      accessCode,
      saleItemId: saleItem?.saleItemId,
      trialSaleItemId: saleItem?.trialSaleItemId,
    });
    try {
      task.start();
      dispatch(setCheckoutCustomerDetails(undefined));
      await createOrder();
      task.success();
    } catch (ex) {
      task.failed(ex);
      dispatch(setHasInitializationFailed(true));
      captureException(ex);
      dispatch(
        updateCheckoutState({
          purchaseErrorCode: errorConstants.errorCreateOrder,
        })
      );
    }
  }

  /**
   * Executes an order directly via billing-core.
   */
  async function executeOrder() {
    const { order } = getStore().getState().checkoutOrders;
    if (!order?.uuid) {
      throw new Error("No order to execute");
    }

    try {
      return await executeOrderRequest(order.uuid);
    } catch (ex) {
      captureException(ex);
      dispatch(
        updateCheckoutState({
          purchaseErrorCode: errorConstants.errorCreateOrder,
        })
      );
      throw ex;
    }
  }

  return {
    initializeCheckout: useCallbackRef(initializeCheckout),
    createOrder: useCallbackRef(createOrder),
    setOrder: useCallbackRef(setOrder),
    createPurchaseOrder: useCallbackRef(createPurchaseOrder),
    createSwitchOrder: useCallbackRef(createSwitchOrder),
    getOrder: useCallbackRef(getOrder),
    executeOrder: useCallbackRef(executeOrder),
  };
}
